import { useEffect, useMemo, useState } from "react";
import { useToast } from "../../../../shared/toastMessages/toastProvider";

import {
  Typography,
  Divider,
  Stack,
  Button,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  CircularProgress,
} from "@mui/material";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";

import { theme } from "../../../../../configs/theme";
import getApiData from "../../../../../hooks/apiRequests/getRequest";
import putApiData from "../../../../../hooks/apiRequests/putRequest";
import EmailEditor from "./emailEditor";

const cardSx = {
  bgcolor: "#fff",
  borderRadius: "15px",
  boxShadow: "1px 5px 5px 1px rgba(0,0,0,0.12)",
  p: { xs: 2, md: 3 },
};

const General = () => {
  const { showToast } = useToast();

  const [settings, setSettings] = useState([]);
  const [malingEmail, setMailingEmail] = useState("");
  const [greeting, setGreeting] = useState("");
  const [signoff, setSignoff] = useState("");
  const [initial, setInitial] = useState({
    email: "",
    greeting: "",
    signoff: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await getApiData("/Settings");
        const data = response?.data || [];

        setSettings(data);

        const getValue = (key) =>
          data.find((s) => s.name === key)?.config_value || "";

        const email = getValue("sending_email");
        const welcome = getValue("welcome_messgae");
        const signOff = getValue("sign_off_message");

        setMailingEmail(email);
        setGreeting(welcome);
        setSignoff(signOff);
        setInitial({ email, greeting: welcome, signoff: signOff });
      } catch (error) {
        console.error(error);
        showToast("Failed to load settings", "error");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [showToast]);

  const hasChanges = useMemo(
    () =>
      malingEmail !== initial.email ||
      greeting !== initial.greeting ||
      signoff !== initial.signoff,
    [malingEmail, greeting, signoff, initial]
  );

  const handleSaveGeneral = async () => {
    if (!hasChanges || saving) return;

    setSaving(true);
    try {
      const valueByName = {
        sending_email: malingEmail,
        welcome_messgae: greeting,
        sign_off_message: signoff,
      };

      // Only persist the general email settings — avoid rewriting unrelated configs.
      const updatedSettings = settings
        .filter((s) => Object.prototype.hasOwnProperty.call(valueByName, s.name))
        .map((s) => ({ ...s, config_value: valueByName[s.name] }));

      if (updatedSettings.length === 0) {
        throw new Error("Required settings rows were not found");
      }

      const response = await putApiData("/Update/Settings", updatedSettings);
      if (!response?.data) {
        throw new Error("Empty settings update response");
      }

      setSettings((prev) =>
        prev.map((s) =>
          Object.prototype.hasOwnProperty.call(valueByName, s.name)
            ? { ...s, config_value: valueByName[s.name] }
            : s
        )
      );
      setInitial({
        email: malingEmail,
        greeting,
        signoff,
      });

      showToast("Settings saved successfully", "success");
    } catch (err) {
      console.error(err);
      showToast("Failed to save settings", "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Paper elevation={0} sx={cardSx}>
        <Box
          sx={{
            py: 8,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <CircularProgress color="primary" size={28} />
          <Typography variant="body2" color="text.secondary">
            Loading settings...
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Stack spacing={2}>
      <Paper elevation={0} sx={cardSx}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          spacing={1.5}
          sx={{ mb: 2 }}
        >
          <Box>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <EmailOutlinedIcon fontSize="small" color="action" />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                General email settings
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              Choose the sending address and default greeting and sign-off content.
            </Typography>
          </Box>

          <Button
            variant="contained"
            theme={theme}
            onClick={handleSaveGeneral}
            disabled={!hasChanges || saving}
            startIcon={<SaveOutlinedIcon />}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "10px",
              px: 2.5,
              alignSelf: { xs: "stretch", sm: "center" },
            }}
          >
            {saving ? "Saving..." : "Save changes"}
          </Button>
        </Stack>

        <Divider sx={{ mb: 3 }} />

        <Stack spacing={3}>
          <Box>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mb: 1, fontWeight: 600 }}
            >
              Sending email
            </Typography>
            <FormControl fullWidth size="small">
              <InputLabel id="sending-email-label">Sending email</InputLabel>
              <Select
                labelId="sending-email-label"
                value={malingEmail}
                label="Sending email"
                onChange={(e) => setMailingEmail(e.target.value)}
                sx={{ borderRadius: "10px" }}
              >
                <MenuItem value="adam.mcauliffe@acgfreight.co.uk">
                  adam.mcauliffe@acgfreight.co.uk
                </MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Divider />

          <Box>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mb: 1, fontWeight: 600 }}
            >
              Greeting
            </Typography>
            <EmailEditor value={greeting} onChange={setGreeting} />
          </Box>

          <Divider />

          <Box>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mb: 1, fontWeight: 600 }}
            >
              Sign off
            </Typography>
            <EmailEditor value={signoff} onChange={setSignoff} />
          </Box>

          <Typography variant="caption" color="text.secondary">
            {hasChanges ? "You have unsaved changes." : "No changes to save."}
          </Typography>
        </Stack>
      </Paper>
    </Stack>
  );
};

export default General;
